<template>
    <div class="events-page" v-reveal="{ anim: 'unfold', stagger: 90, y: 28 }">
        <!-- 顶部统计：总数 + 类型 Top -->
        <section class="stats-grid" v-reveal="{ anim: 'pop', stagger: 80, y: 22 }">
            <StatCard label="事件总数" :value="total" icon="📊" accent="violet" hint="符合当前筛选" />
            <StatCard v-for="s in topCards" :key="s.event_type" :label="s.label" :value="s.count"
                :accent="s.accent" :icon="s.icon" hint="按类型聚合" />
        </section>

        <!-- 筛选工具栏 -->
        <section class="toolbar card neon">
            <div class="filters">
                <div class="field">
                    <div class="cap">事件类型</div>
                    <select class="input" v-model="q.type">
                        <option value="">全部类型</option>
                        <option v-for="t in TYPES" :key="t" :value="t">{{ t }}</option>
                    </select>
                </div>
                <div class="field">
                    <div class="cap">卡密</div>
                    <input class="input" v-model="q.key" placeholder="卡密关键字" />
                </div>
                <div class="field">
                    <div class="cap">IP 地址</div>
                    <input class="input" v-model="q.ip" placeholder="IP 关键字" />
                </div>
                <div class="field">
                    <div class="cap">HWID</div>
                    <input class="input" v-model="q.hwid_hash" placeholder="HWID 关键字" />
                </div>
                <div class="field">
                    <div class="cap">开始时间</div>
                    <input class="input" v-model="q.from" placeholder="YYYY-MM-DD" />
                </div>
                <div class="field">
                    <div class="cap">结束时间</div>
                    <input class="input" v-model="q.to" placeholder="YYYY-MM-DD" />
                </div>
                <div class="field">
                    <div class="cap">每页</div>
                    <select class="input" v-model.number="pageSize">
                        <option :value="20">20 条</option>
                        <option :value="50">50 条</option>
                        <option :value="100">100 条</option>
                    </select>
                </div>
                <div class="field f-actions">
                    <button class="btn btn-primary" @click="apply">应用</button>
                    <button class="btn" @click="resetFilters">重置</button>
                </div>
            </div>
        </section>

        <!-- 视图与分页条 -->
        <section class="bar card neon">
            <div class="view-switch">
                <button :class="{ on: view === 'grid' }" @click="view = 'grid'" title="卡片视图">▦</button>
                <button :class="{ on: view === 'table' }" @click="view = 'table'" title="表格视图">≣</button>
            </div>
            <div class="pager">
                <button class="btn btn-mini" :disabled="page <= 1" @click="page--; load()">上一页</button>
                <span class="badge"><b>{{ page }}</b> / {{ pages }}</span>
                <button class="btn btn-mini" :disabled="page >= pages" @click="page++; load()">下一页</button>
            </div>
        </section>

        <!-- 卡片视图 -->
        <div v-if="view === 'grid'" class="cards">
            <TiltCard v-for="(it, i) in items" :key="it.id" :max="6" :disabled="isTouch" class="ev-card">
                <div class="card-in" :style="{ animationDelay: Math.min(i * 0.045, 0.7) + 's' }">
                    <div class="ev-top">
                        <span class="pill" :class="pill(it.event_type)">{{ it.event_type }}</span>
                        <span class="ev-time muted2">{{ fmtTime(it.created_at) }}</span>
                    </div>
                    <div class="ev-detail" :title="it.detail">{{ it.detail || '无详情' }}</div>
                    <div class="ev-rows">
                        <div class="ev-row" v-if="it.license_key">
                            <span class="muted2">卡密</span>
                            <span class="mono ellipsis" :title="it.license_key">{{ it.license_key }}</span>
                        </div>
                        <div class="ev-row" v-if="it.hwid_hash">
                            <span class="muted2">HWID</span>
                            <span class="mono ellipsis" :title="it.hwid_hash">{{ it.hwid_hash }}</span>
                        </div>
                        <div class="ev-row" v-if="it.ip">
                            <span class="muted2">IP</span>
                            <span class="mono">{{ it.ip }}</span>
                        </div>
                    </div>
                </div>
            </TiltCard>
        </div>

        <!-- 表格视图 -->
        <div v-else class="table-card card neon">
            <div class="table-scroll">
                <table class="table">
                    <thead>
                        <tr>
                            <th>类型</th>
                            <th>卡密</th>
                            <th>HWID</th>
                            <th>IP</th>
                            <th>详情 / 时间</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="it in items" :key="it.id" class="row-hover">
                            <td><span class="pill" :class="pill(it.event_type)">{{ it.event_type }}</span></td>
                            <td class="mono"><div class="ellipsis" :title="it.license_key">{{ it.license_key || '-' }}</div></td>
                            <td class="mono muted"><div class="ellipsis" :title="it.hwid_hash">{{ it.hwid_hash || '-' }}</div></td>
                            <td class="mono muted"><div class="ellipsis" :title="it.ip">{{ it.ip || '-' }}</div></td>
                            <td>
                                <div class="detail-text" :title="it.detail">{{ it.detail || '-' }}</div>
                                <div class="small muted2">{{ fmtTime(it.created_at) }}</div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <EmptyState v-if="!items.length" title="暂无事件数据" desc="调整筛选条件后点击“应用”重新查询。" />
    </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, reactive, ref, watch } from "vue";
import { eventsApi } from "../api/events";
import StatCard from "../components/StatCard.vue";
import TiltCard from "../components/TiltCard.vue";
import EmptyState from "../components/EmptyState.vue";

const inst = getCurrentInstance();
function notify(type, title, message) {
    inst?.root?.proxy?.$toast?.push?.({ type, title, message });
}

const isTouch = typeof window !== "undefined" && "ontouchstart" in window;

// 事件类型枚举，与后端约定一致
const TYPES = [
    "TAMPERED", "TOOL_DETECTED", "BAD_PROOF", "INVALID", "BLACKLIST", "BANNED", "OK",
    "NO_CLIENT_VERSION", "NO_BUILD_ID", "INVALID_BUILD", "BUILD_BLOCKED",
    "BUILD_VERSION_MISMATCH", "BUILD_HASH_MISMATCH", "FORCE_UPDATE",
];

const q = reactive({ type: "", key: "", ip: "", hwid_hash: "", from: "", to: "" });
const page = ref(1);
const pageSize = ref(50);
const view = ref("grid");
const items = ref([]);
const total = ref(0);
const byType = ref([]);

const pages = computed(() => Math.max(1, Math.ceil((total.value || 0) / (pageSize.value || 50))));

// 顶部固定渲染 3 张类型卡，不足时用占位补齐，保证统计区四列对齐
const topCards = computed(() => {
    const top = (byType.value || []).slice(0, 3).map((s) => ({
        event_type: s.event_type,
        label: s.event_type,
        count: s.count,
        accent: accentOf(s.event_type),
        icon: iconOf(s.event_type),
    }));
    while (top.length < 3) {
        top.push({ event_type: "__ph" + top.length, label: "—", count: 0, accent: "cyan", icon: "·" });
    }
    return top;
});

// 改每页条数时回到第 1 页重查
watch(pageSize, () => apply());

function fmtTime(v) {
    if (!v) return "-";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// 危险类（红）/ 警告类（黄）/ 正常（绿）的分类，沿用旧规则
const DANGER = ["TAMPERED", "TOOL_DETECTED", "BANNED"];
const WARN = [
    "BAD_PROOF", "INVALID", "BLACKLIST", "NO_CLIENT_VERSION", "NO_BUILD_ID",
    "INVALID_BUILD", "BUILD_BLOCKED", "BUILD_VERSION_MISMATCH", "BUILD_HASH_MISMATCH", "FORCE_UPDATE",
];

function pill(t) {
    if (DANGER.includes(t)) return "pill-bad";
    if (WARN.includes(t)) return "pill-warn";
    if (t === "OK") return "pill-good";
    return "pill-ok";
}

function accentOf(t) {
    if (DANGER.includes(t)) return "pink";
    if (WARN.includes(t)) return "amber";
    if (t === "OK") return "mint";
    return "cyan";
}

function iconOf(t) {
    if (DANGER.includes(t)) return "🛑";
    if (WARN.includes(t)) return "⚠";
    if (t === "OK") return "✓";
    return "•";
}

async function load() {
    try {
        const params = { ...q, page: page.value, pageSize: pageSize.value };
        const out = await eventsApi.list(params);
        if (!out?.ok) {
            notify("bad", "事件加载失败", out?.code || "FAILED");
            return;
        }
        items.value = out.items || [];
        total.value = out.total || 0;

        // 统计：按类型聚合（按小时分组），用于顶部 Top 卡片
        const stats = await eventsApi.stats({
            from: q.from,
            to: q.to,
            group: "hour",
            type: q.type || undefined,
        });
        if (stats?.ok) byType.value = stats.by_type || [];
    } catch (e) {
        notify("bad", "网络错误", String(e?.message || e));
    }
}

function apply() {
    page.value = 1;
    load();
}

function resetFilters() {
    q.type = "";
    q.key = "";
    q.ip = "";
    q.hwid_hash = "";
    q.from = "";
    q.to = "";
    page.value = 1;
    pageSize.value = 50;
    load();
}

onMounted(load);
</script>

<style scoped>
.events-page {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
}

.toolbar {
    padding: 14px;
}

.filters {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    align-items: end;
}

.f-actions {
    display: flex;
    gap: 8px;
    align-items: flex-end;
}

.f-actions .btn {
    flex: 1 1 auto;
}

.bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
}

.view-switch {
    display: inline-flex;
    border: 1px solid var(--stroke);
    border-radius: 10px;
    overflow: hidden;
}

.view-switch button {
    width: 38px;
    height: 38px;
    background: transparent;
    border: none;
    color: var(--muted);
    cursor: pointer;
    font-size: 16px;
}

.view-switch button.on {
    background: var(--grad-violet-cyan);
    color: #fff;
}

.pager {
    display: flex;
    align-items: center;
    gap: 8px;
}

.cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 14px;
}

.ev-card {
    padding: 16px;
}

.ev-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
}

.ev-time {
    font-size: 12px;
    white-space: nowrap;
}

.ev-detail {
    font-size: 13.5px;
    line-height: 1.45;
    word-break: break-word;
    margin-bottom: 10px;
    min-height: 20px;
}

.ev-rows {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.ev-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 12.5px;
    padding-top: 6px;
    border-top: 1px solid var(--stroke-soft);
}

.ev-row .ellipsis {
    max-width: 200px;
}

.ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 220px;
}

.table-card {
    padding: 6px;
}

.table-scroll {
    overflow: auto;
    border-radius: var(--r-md);
    max-height: 70vh;
}

.detail-text {
    white-space: normal;
    word-break: break-word;
    line-height: 1.35;
    max-width: 460px;
}

.small {
    font-size: 12px;
    margin-top: 4px;
}

@media (max-width: 1100px) {
    .stats-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .filters {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 768px) {
    .filters {
        grid-template-columns: 1fr;
    }

    .cards {
        grid-template-columns: 1fr;
    }
}
</style>
